/**
 * Standard measurement units for DNR Vyapar.
 *
 * Each unit has a stable internal code and a user-facing display label.
 * Codes are used internally; labels are shown to users.
 * This separation allows labels to change without breaking stored data.
 */
export interface Unit {
  readonly code: string;
  readonly label: string;
}

/**
 * Standard measurement units available for selection.
 * Codes are stable internal identifiers; labels are user-facing.
 */
export const STANDARD_UNITS: readonly Unit[] = [
  { code: 'NOS', label: 'Nos' },
  { code: 'SQFT', label: 'Sq.ft' },
  { code: 'SQM', label: 'Sq.m' },
  { code: 'RFT', label: 'RFT' },
  { code: 'RMT', label: 'RMT' },
  { code: 'KG', label: 'Kg' },
  { code: 'LTR', label: 'Ltr' },
  { code: 'SET', label: 'Set' },
  { code: 'PROJECT', label: 'Project' },
  { code: 'LUMP_SUM', label: 'Lump Sum' },
] as const;

/**
 * Default unit for new products/lines when no specific unit is set.
 */
export const DEFAULT_UNIT = 'NOS';

/**
 * Lookup a unit's display label by its code.
 * Falls back to the code itself if not found (preserves backward compatibility).
 */
export function getUnitLabel(code: string): string {
  const unit = STANDARD_UNITS.find((u) => u.code === code);
  return unit?.label ?? code;
}

/**
 * Lookup a unit's code by its display label.
 * Falls back to the label itself if not found.
 */
export function getUnitCode(label: string): string {
  const unit = STANDARD_UNITS.find((u) => u.label === label);
  return unit?.code ?? label;
}

/**
 * Check if a unit code is a known standard unit.
 */
export function isStandardUnit(code: string): boolean {
  return STANDARD_UNITS.some((u) => u.code === code);
}

/**
 * All valid unit codes as a flat array (for validation arrays).
 */
export const UNIT_CODES = STANDARD_UNITS.map((u) => u.code);

/**
 * Default unit precision for each unit type (decimal places).
 * Used by Product.unitPrecision.
 */
export const UNIT_PRECISION: Record<string, number> = {
  NOS: 0,
  SQFT: 2,
  SQM: 2,
  RFT: 2,
  RMT: 2,
  KG: 3,
  LTR: 3,
  SET: 0,
  PROJECT: 0,
  LUMP_SUM: 0,
} as const;

/**
 * Get default precision for a unit code.
 * Defaults to 0 (whole numbers) if not specified.
 */
export function getDefaultPrecision(unitCode: string): number {
  return UNIT_PRECISION[unitCode] ?? 0;
}

/**
 * Validate a unit code against standard units.
 * Returns the normalized code or the original if not standard.
 * This preserves backward compatibility with custom/legacy units.
 */
export function normalizeUnit(code: string): string {
  const upper = code.trim().toUpperCase();
  if (isStandardUnit(upper)) return upper;
  return code.trim() || 'NOS';
}

/**
 * Maximum number of item options to show in the picker.
 */
export const ITEM_OPTION_LIMIT = 50;

/**
 * Get filtered product options based on a search query.
 * Includes the currently selected product even if it doesn't match the query.
 */
export function pickerOptions(
  products: readonly ProductListItem[],
  query: string,
  selectedProductId?: string,
): { options: readonly { product: ProductListItem; label: string }[]; matched: number; truncated: boolean } {
  const queryLower = query.trim().toLowerCase();
  let matched = 0;
  const options = products
    .filter((p) => {
      if (p.product.id === selectedProductId) return true;
      if (!query) return true;
      return p.product.name.toLowerCase().includes(queryLower);
    })
    .map((p) => ({ product: p, label: p.product.name }))
    .slice(0, ITEM_OPTION_LIMIT);
  matched = products.filter((p) => {
    if (p.product.id === selectedProductId) return true;
    if (!query) return true;
    return p.product.name.toLowerCase().includes(queryLower);
  }).length;
  return { options, matched, truncated: matched > ITEM_OPTION_LIMIT };
}

import type { ProductListItem } from '@data/services/productReader';